import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [state, setState] = useState({ status: "loading", orderId: null });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const sessionId = params.get("session_id");
    const metodo = params.get("metodo");
    const token = params.get("token");

    const done = (orderId) => {
      clear();
      setState({ status: "paid", orderId });
    };

    if (metodo === "contrassegno") {
      clear();
      setState({ status: "cod", orderId: null });
      return;
    }

    if (metodo === "paypal" && token) {
      api
        .post("/payments/paypal/capture", { paypal_order_id: token })
        .then((r) => done(r.data.order_id))
        .catch(() => setState({ status: "failed", orderId: null }));
      return;
    }
    if (sessionId) {
      let attempts = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            done(data.order_id);
            return;
          }
        } catch {}
        attempts += 1;
        if (attempts < 8) setTimeout(poll, 2000);
        else setState({ status: "failed", orderId: null });
      };
      poll();
      return;
    }
    setState({ status: "failed", orderId: null });
  }, []);

  return (
    <div data-testid="payment-success-page" className="max-w-2xl mx-auto px-6 py-24 text-center">
      {state.status === "loading" && (
        <>
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#ff4500]" strokeWidth={1.5} />
          <h1 className="mt-6 font-display font-medium text-3xl uppercase tracking-tight">Conferma pagamento in corso...</h1>
          <p className="mt-3 text-muted-foreground text-sm">Stiamo verificando il tuo pagamento. Non chiudere la pagina.</p>
        </>
      )}
      {state.status === "cod" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#10b981]" strokeWidth={1.5} />
          <h1 data-testid="cod-success-title" className="mt-6 font-display font-medium text-3xl sm:text-4xl uppercase tracking-tight leading-tight">
            Ordine in contrassegno effettuato con successo
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Il suo ordine sarà in lavorazione.<br />
            <strong>Grazie per aver effettuato il suo acquisto con noi.</strong>
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link data-testid="cod-orders-link" to="/account" className="btn-hard">Vedi i miei ordini</Link>
            <Link data-testid="cod-catalog-link" to="/catalogo" className="btn-outline-hard">Continua lo shopping</Link>
          </div>
        </>
      )}
      {state.status === "paid" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[#10b981]" strokeWidth={1.5} />
          <h1 data-testid="payment-success-title" className="mt-6 font-display font-medium text-4xl uppercase tracking-tight">
            Ordine confermato
          </h1>
          <p className="mt-3 text-muted-foreground">
            Grazie per il tuo acquisto. Il tuo ordine è in preparazione e partirà entro 24 ore.
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link data-testid="success-orders-link" to="/account" className="btn-hard">Vedi i miei ordini</Link>
            <Link data-testid="success-catalog-link" to="/catalogo" className="btn-outline-hard">Continua lo shopping</Link>
          </div>
        </>
      )}
      {state.status === "failed" && (
        <>
          <XCircle className="w-14 h-14 mx-auto text-destructive" strokeWidth={1.5} />
          <h1 className="mt-6 font-display font-medium text-3xl uppercase tracking-tight">Pagamento non confermato</h1>
          <p className="mt-3 text-muted-foreground">Non siamo riusciti a confermare il pagamento. Controlla i tuoi ordini o riprova.</p>
          <Link data-testid="failed-account-link" to="/account" className="btn-dark mt-8 inline-flex">I miei ordini</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div data-testid="payment-cancel-page" className="max-w-2xl mx-auto px-6 py-24 text-center">
      <XCircle className="w-14 h-14 mx-auto text-[#ff4500]" strokeWidth={1.5} />
      <h1 className="mt-6 font-display font-medium text-4xl uppercase tracking-tight">Pagamento annullato</h1>
      <p className="mt-3 text-muted-foreground">Nessun addebito è stato effettuato. Il tuo carrello è ancora lì che ti aspetta.</p>
      <div className="mt-8 flex justify-center gap-3 flex-wrap">
        <Link data-testid="cancel-cart-link" to="/carrello" className="btn-hard">Torna al carrello</Link>
        <Link data-testid="cancel-catalog-link" to="/catalogo" className="btn-outline-hard">Catalogo</Link>
      </div>
    </div>
  );
}
